import React, { useState, useRef, useEffect, useCallback } from 'react';
import './Popover.css';

export type PopoverPlacement = 'top' | 'bottom' | 'left' | 'right';

export interface PopoverProps {
  /** The content to display inside the popover */
  content: React.ReactNode;
  /** The trigger element that opens the popover */
  children: React.ReactElement;
  /** Placement of the popover relative to target */
  placement?: PopoverPlacement;
  /** Whether the popover is open (controlled mode) */
  isOpen?: boolean;
  /** Callback when popover should open/close (controlled mode) */
  onInteraction?: (isOpen: boolean) => void;
  /** Whether clicking outside closes the popover */
  closeOnClickOutside?: boolean;
  /** Whether pressing Escape closes the popover */
  closeOnEscape?: boolean;
  /** Additional CSS class for the popover */
  className?: string;
  /** Additional CSS class for the popover content */
  contentClassName?: string;
  /** Whether to show an arrow pointing to the target */
  hasArrow?: boolean;
  /** Offset from the target in pixels */
  offset?: number;
}

/**
 * Popover component for displaying floating content anchored to a trigger element.
 * Inspired by Blueprint's Popover with click-to-toggle interaction.
 */
export const Popover: React.FC<PopoverProps> = ({
  content,
  children,
  placement = 'bottom',
  isOpen: controlledIsOpen,
  onInteraction,
  closeOnClickOutside = true,
  closeOnEscape = true,
  className = '',
  contentClassName = '',
  hasArrow = true,
  offset = 8,
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const isControlled = controlledIsOpen !== undefined;
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;

  const setIsOpen = useCallback(
    (value: boolean) => {
      if (isControlled) {
        onInteraction?.(value);
      } else {
        setInternalIsOpen(value);
      }
    },
    [isControlled, onInteraction]
  );

  const handleToggle = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen, setIsOpen]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  // Handle click outside
  useEffect(() => {
    if (!isOpen || !closeOnClickOutside) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current &&
        !triggerRef.current.contains(target) &&
        popoverRef.current &&
        !popoverRef.current.contains(target)
      ) {
        handleClose();
      }
    };

    // Delay adding listener to avoid immediate close from the click that opened it
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen, closeOnClickOutside, handleClose]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeOnEscape, handleClose]);

  // Calculate position
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!isOpen || !triggerRef.current || !popoverRef.current) return;

    const trigger = triggerRef.current;
    const popover = popoverRef.current;
    const triggerRect = trigger.getBoundingClientRect();
    const popoverRect = popover.getBoundingClientRect();

    let top = 0;
    let left = 0;

    switch (placement) {
      case 'top':
        top = triggerRect.top - popoverRect.height - offset;
        left = triggerRect.left + (triggerRect.width - popoverRect.width) / 2;
        break;
      case 'bottom':
        top = triggerRect.bottom + offset;
        left = triggerRect.left + (triggerRect.width - popoverRect.width) / 2;
        break;
      case 'left':
        top = triggerRect.top + (triggerRect.height - popoverRect.height) / 2;
        left = triggerRect.left - popoverRect.width - offset;
        break;
      case 'right':
        top = triggerRect.top + (triggerRect.height - popoverRect.height) / 2;
        left = triggerRect.right + offset;
        break;
    }

    // Clamp to viewport
    const padding = 8;
    left = Math.max(padding, Math.min(left, window.innerWidth - popoverRect.width - padding));
    top = Math.max(padding, Math.min(top, window.innerHeight - popoverRect.height - padding));

    setPosition({ top, left });
  }, [isOpen, placement, offset]);

  // Clone the child element to attach click handler and ref
  const trigger = React.cloneElement(children, {
    onClick: (e: React.MouseEvent) => {
      children.props.onClick?.(e);
      handleToggle();
    },
    'aria-expanded': isOpen,
    'aria-haspopup': true,
  });

  return (
    <>
      <div ref={triggerRef} className="stc-popover-trigger">
        {trigger}
      </div>
      {isOpen && (
        <div
          ref={popoverRef}
          className={`stc-popover stc-popover--${placement} ${hasArrow ? 'stc-popover--has-arrow' : ''} ${className}`}
          style={{
            position: 'fixed',
            top: position.top,
            left: position.left,
          }}
          role="dialog"
          aria-modal="false"
        >
          {hasArrow && <div className="stc-popover__arrow" />}
          <div className={`stc-popover__content ${contentClassName}`}>{content}</div>
        </div>
      )}
    </>
  );
};
